import {
  MutationFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { DataResponse, meKeys } from "@common/features/User";
import { api } from "@common/helpers/api";
import { markPwaInstallMilestone } from "@common/helpers/pwa";
import { SuccessResponse } from "@common/interfaces/Api";
import {
  getSafeRedirectLocation,
  getSafeRedirectPath,
} from "@app/helpers/safeRedirect";
import { useLocalStorage } from "@app/hooks";

interface LoginResponse {
  "2fa_needed"?: boolean;
  success?: boolean;
}

export const useLogin = () =>
  useMutation({
    mutationFn: (data: {
      email: string;
      password: string;
      "g-recaptcha-response"?: string;
    }) =>
      api
        .post<LoginResponse>("user/login", data, { timeout: 30000 })
        .then((response) => response.data),
  });

export const useLogout = () =>
  useUserStateMutation(
    () =>
      api
        .post<SuccessResponse>("user/logout")
        .then((response) => response.data),
    {
      loggedOut: true,
    },
  );

export const useSignUp = () =>
  useUserStateMutation(
    (data: {
      name: string;
      email: string;
      password: string;
      "g-recaptcha-response"?: string;
      affiliateId?: string;
      implicit_name?: string | null;
      implicit_email?: string | null;
      implicit_mobile?: string | null;
      client_id?: string | null;
    }) =>
      api
        .post<SuccessResponse>("user/register", data)
        .then((response) => response.data),
    {
      signedup: true,
    },
  );

export const useGoogleLogin = () =>
  useUserStateMutation(
    (data: { idToken: string; affiliateId?: string }) =>
      api
        .post<SuccessResponse & { new_user: boolean }>("user/google", data)
        .then((response) => response.data),
    {
      isGoogle: true,
    },
  );

export const useFacebookLogin = () =>
  useUserStateMutation(
    (data: { token: string; affiliateId?: string }) =>
      api
        .post<SuccessResponse & { new_user: boolean }>("user/facebook", data)
        .then((response) => response.data),
    {
      isFacebook: true,
    },
  );

export const useCompleteProfile = () =>
  useUserStateMutation(
    (data: { mobile: string }) =>
      api
        .post<SuccessResponse>("user/complete", data)
        .then((response) => response.data),
    {
      completed: true,
    },
  );

export const useSendVerifyMobile = () =>
  useMutation({
    mutationFn: () =>
      api
        .post<SuccessResponse>("user/send/sms")
        .then((response) => response.data),
  });

export const useVerifyMobile = () =>
  useUserStateMutation(
    (data: { code: string; "g-recaptcha-response"?: string }) =>
      api
        .post<SuccessResponse>("user/verify/mobile", data)
        .then((response) => response.data),
    {
      mobileVerified: true,
    },
  );

export const useSendVerifyEmail = () =>
  useMutation({
    mutationFn: () =>
      api
        .post<SuccessResponse>("user/send/email")
        .then((response) => response.data),
  });

export const useVerifyEmail = () =>
  useUserStateMutation((data: { hash: string | null }) =>
    api
      .post<SuccessResponse>("user/verify/email", data)
      .then((response) => response.data),
  );

export const useSendRecoverPassword = () =>
  useMutation({
    mutationFn: (data: {
      "g-recaptcha-response"?: string;
      emailOrMobile: string;
    }) =>
      api
        .post<SuccessResponse>("user/recovery", data)
        .then((response) => response.data),
  });

export const useVerifyRecoveryCode = () =>
  useMutation({
    mutationFn: (data: {
      emailOrMobile: string;
      code: string;
      "g-recaptcha-response"?: string;
    }) =>
      api
        .post<SuccessResponse>("user/recovery/verify", data)
        .then((response) => response.data),
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: (data: {
      emailOrMobile: string;
      code: string;
      password: string;
    }) =>
      api
        .post<SuccessResponse>("user/recovery/reset", data)
        .then((response) => response.data),
  });

export const useSendOtp = () =>
  useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api
        .post<SuccessResponse & { otp_token?: string }>("user/send/otp", data)
        .then((response) => response.data),
  });

export const useVerifyOtp = () =>
  useUserStateMutation(
    (data: {
      code: number;
      otp_token: string;
      "g-recaptcha-response"?: string;
    }) =>
      api
        .post<SuccessResponse>("user/verify-otp", data)
        .then((response) => response.data),
    {
      loggedIn: true,
    },
  );

function useUserStateMutation<
  TData extends SuccessResponse & { new_user?: boolean },
  TVariables = void,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  state?: {
    loggedOut?: boolean;
    loggedIn?: boolean;
    signedup?: boolean;
    completed?: boolean;
    mobileVerified?: boolean;
    isGoogle?: boolean;
    isFacebook?: boolean;
  },
) {
  const queryClient = useQueryClient();
  const history = useHistory();

  const [, setInstalledVersion] = useLocalStorage("installed_version", "");

  return useMutation({
    mutationFn,
    onSuccess: async (response) => {
      if (response.success !== true) return;

      await queryClient.refetchQueries(
        { queryKey: meKeys.data.queryKey },
        { throwOnError: true },
      );

      const data = queryClient.getQueryData<DataResponse>(meKeys.data.queryKey);

      if (!data) return;

      if (!state) return;

      // this.setState({ data: {} as any });
      const params = new URLSearchParams(history.location.search);
      const redirect = getSafeRedirectPath(params.get("redirect"));

      if (state.isGoogle) {
        state.signedup = response.new_user;
        state.loggedIn = !response.new_user;
      }

      if (state.isFacebook) {
        state.signedup = response.new_user;
        state.loggedIn = !response.new_user;
      }

      if (state.loggedIn) {
        markPwaInstallMilestone("logged-in");
      }

      if (state.mobileVerified) {
        setInstalledVersion(data.webapp.version);

        history.replace({
          ...getSafeRedirectLocation(redirect),
          search: "firstlogin=1",
        });
        return;
      }

      if (state.signedup) {
        setInstalledVersion(data.webapp.version);

        history.replace({
          ...getSafeRedirectLocation(redirect),
        });
        return;
      }

      if (state.completed) {
        history.replace({
          pathname: "/verify",
          search: "redirect=" + encodeURIComponent(redirect),
        });
        return;
      }

      if (state.loggedIn) {
        history.replace({
          ...getSafeRedirectLocation(redirect),
        });
      }
    },
  });
}
